import { useEffect, useRef, useState } from 'react';

/**
 * Dependency-free emoji picker. A small categorised set covering the
 * overwhelming majority of chat usage — no 4000-emoji bundle, no network.
 */
const CATEGORIES: { label: string; icon: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    icon: '😀',
    emojis: [
      '😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊',
      '😋', '😎', '😍', '😘', '🥰', '😗', '🙂', '🤗', '🤩', '🤔',
      '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯',
      '😪', '😫', '🥱', '😴', '😌', '😛', '😜', '🤪', '😝', '🤤',
      '😒', '😓', '😔', '😕', '🙃', '🤑', '😲', '🙁', '😖', '😞',
      '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬',
      '😰', '😱', '🥵', '🥶', '😳', '🤪', '😵', '🥴', '😠', '😡',
    ],
  },
  {
    label: 'Gestures',
    icon: '👍',
    emojis: [
      '👍', '👎', '👌', '🤌', '✌️', '🤞', '🤟', '🤘', '👏', '🙌',
      '👐', '🤲', '🙏', '✍️', '💪', '👈', '👉', '👆', '👇', '☝️',
      '✋', '🤚', '🖐️', '🖖', '👋', '🤙', '💅', '👀', '🫶', '🤝',
    ],
  },
  {
    label: 'Hearts',
    icon: '❤️',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '♥️',
    ],
  },
  {
    label: 'Fun',
    icon: '🎉',
    emojis: [
      '🎉', '🎊', '🥳', '🎁', '🎈', '✨', '🌟', '⭐', '🔥', '💯',
      '💫', '⚡', '🏆', '🥇', '🎯', '🎲', '🎰', '🃏', '💰', '💸',
      '💎', '👑', '🚀', '🎬', '🎮', '🍀', '🌈', '☀️', '🌙', '💥',
    ],
  },
  {
    label: 'Food',
    icon: '🍕',
    emojis: [
      '🍕', '🍔', '🍟', '🌭', '🍿', '🍩', '🍪', '🎂', '🍰', '🧁',
      '🍫', '🍬', '🍭', '☕', '🍵', '🍺', '🍻', '🥂', '🍷', '🥃',
      '🍸', '🍹', '🧉', '🥤', '🍾', '🍎', '🍓', '🍇', '🍉', '🍌',
    ],
  },
];

interface Props {
  onPick: (emoji: string) => void;
}

export function EmojiPicker({ onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Esc. composedPath() is used instead of
  // contains() because inside a shadow DOM the event target is retargeted
  // to the host element, which would make contains() always false and
  // close the picker on the same click that opened it.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const path = e.composedPath ? e.composedPath() : [];
      if (wrapRef.current && !path.includes(wrapRef.current)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // Defer registration so the opening click doesn't immediately close it.
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative shrink-0">
      {open && (
        <div className="absolute bottom-10 right-0 z-20 w-72 rounded-xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden">
          <div className="flex border-b border-gray-100">
            {CATEGORIES.map((c, i) => (
              <button
                key={c.label}
                type="button"
                aria-label={c.label}
                onClick={() => setCat(i)}
                className={`flex-1 py-2 text-base transition ${i === cat ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              >
                {c.icon}
              </button>
            ))}
          </div>
          <div className="max-h-48 overflow-y-auto p-2 grid grid-cols-8 gap-1">
            {CATEGORIES[cat].emojis.map(e => (
              <button
                key={e}
                type="button"
                onClick={() => { onPick(e); setOpen(false); }}
                className="text-xl leading-none p-1 rounded hover:bg-gray-100 transition"
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
      <button
        type="button"
        aria-label="Insert emoji"
        onClick={() => setOpen(v => !v)}
        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      </button>
    </div>
  );
}
